import { IAction } from 'legacy/lut/classes/models/onboarding/IAction'

import { IForm } from 'core-components/classes/models/IForm'
import WebSocketStore from 'core/classes/services/WebSocketStore'
import Logger from 'core/utils/Logger'
import {
  IInvalidParamsErrorResponsePayload,
  IResponseErrorPayload,
} from 'onboarding/types/response'
import {
  throwOnErrorWithoutPayload,
  throwOnInvalidRequestParams,
} from 'onboarding/utils/errorResponse/throwOnApiError'

export type TExecutableTransitions = Record<string, Record<string, boolean>>

const CONTROLLER = 'action'

class OnboardingActionApi {
  public getActionsLastHistory = async (actionIds: number[]) => {
    let result = null
    try {
      const response = await WebSocketStore.get('onboarding').call<Record<number, string>>({
        service: CONTROLLER,
        action: 'getActionLastHistory',
        payload: { actionIds },
        withoutTokenRefresh: true,
        isAuth: true,
      })

      if (Object.keys(response.data).length) {
        result = response.data
      }
    } catch (e) {
      Logger.error(e)
    }

    return result
  }

  public getActionLastErrorHistories = async (actionIds: number[]) => {
    const response = await WebSocketStore.get('onboarding').call<{
      reasons: Record<number, string>
    }>({
      service: CONTROLLER,
      action: 'getActionLastErrorHistories',
      payload: { actionIds },
      withoutTokenRefresh: true,
      isAuth: true,
    })

    if (response) {
      return response.data
    }
    return null
  }

  public getExecutableTransitions = async (requestId: number) => {
    const response = await WebSocketStore.get('onboarding').call<TExecutableTransitions>({
      service: CONTROLLER,
      action: 'getExecutableTransitions',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
    })

    if (response) {
      return response.data
    }
    return null
  }

  public getErrorsCount = async (): Promise<number | null> => {
    let result = 0

    try {
      const response = await WebSocketStore.get('onboarding').call<{ count: number }>({
        service: CONTROLLER,
        action: 'getErrorsCount',
        withoutTokenRefresh: true,
        isAuth: true,
      })

      if (response.status === 'success') {
        result = response.data.count
      }
    } catch (e) {
      Logger.error(e)
    }

    return result
  }

  public getActions = async (requestId: number, inheritable?: number) => {
    const response = await WebSocketStore.get('onboarding').call<IForm>({
      service: CONTROLLER,
      action: 'getActions',
      payload: { requestId, inheritable },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })

    return response
  }

  public async fetchWIPActionPopupContent(requestId: number) {
    const response = await WebSocketStore.get('onboarding').call<{ popupContent: string | null }>({
      service: CONTROLLER,
      action: 'getWIPActionPopupContent',
      payload: { requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to fetch action popup content')
    }

    return response.data.popupContent
  }

  public async updateExpirationDate(id: number, expirationDate: string) {
    const response = await WebSocketStore.get('onboarding').call<{ item: IAction }>({
      service: CONTROLLER,
      action: 'updateExpirationDate',
      payload: { id, expirationDate },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Fail')
    }

    return response.data.item
  }

  public async updateAssignedUser(id: number, assignedUser: TSaveAssignedUser) {
    const response = await WebSocketStore.get('onboarding').call<{ item: IAction }>({
      service: CONTROLLER,
      action: 'updateAssignedUser',
      payload: { id, assignedUser },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Fail')
    }

    return response.data.item
  }

  public async updatePopupSettings(id: number, isShowOnceViewed: boolean) {
    const response = await WebSocketStore.get('onboarding').call<{ result: boolean }>({
      service: CONTROLLER,
      action: 'updatePopupSettings',
      payload: { id, isShowOnceViewed },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.result
  }

  public async refreshPopupContent(actionUUID: string, requestId: number) {
    const response = await WebSocketStore.get('onboarding').call<{ refreshed: string | null }>({
      service: CONTROLLER,
      action: 'refreshPopupContent',
      payload: { actionUUID, requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed')
    }

    return response.data.refreshed
  }

  public async reopenOne(
    actionUuid: string,
    requestId: number
  ): Promise<{ isReopened: boolean; item: IAction }> {
    const response = await WebSocketStore.get('onboarding').call<{
      isReopened: boolean
      item: IAction
      errorPayload?: IInvalidParamsErrorResponsePayload | IResponseErrorPayload
    }>({
      service: CONTROLLER,
      action: 'reopenAction',
      payload: { actionUuid, requestId },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
    if (response.status !== 'success') {
      const defaultMessage = 'Failed to reopen action'
      throwOnInvalidRequestParams(response)
      throwOnErrorWithoutPayload(response, defaultMessage)
    }
    const { isReopened, item } = response.data

    return { isReopened, item }
  }

  public async updateExpirationTimer(id: number, hours: number) {
    return WebSocketStore.get('onboarding').call<{ item: IAction }>({
      service: CONTROLLER,
      action: 'updateExpirationTimer',
      payload: { id, hours },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
  }

  public async executeExpirationTimer(id: number) {
    return WebSocketStore.get('onboarding').call<{ status: 'notNeeded' | 'success' }>({
      service: CONTROLLER,
      action: 'executeExpirationTimer',
      payload: { id },
      withoutTokenRefresh: true,
      isAuth: true,
      returnError: true,
    })
  }
}

export type TSaveAssignedUser = {
  selectedPrimaryUser?: string | null
  selectedBackupUser?: string | null
}

export const onboardingActionApi = new OnboardingActionApi()
